import React, { useContext, useEffect, useRef, useState } from 'react';
import './AdyenCardRegistration.less';
import AdyenCheckout from '@adyen/adyen-web';
import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import { DrawerPopup } from '../../dialog-layout/drawer-popup/DrawerPopup';
import { Button } from '../../elements/button/Button';
import { getSafeTranslation } from '../../../helpers/localization.helper';
import { TranslationsContext } from '../../../contexts/translations-context';
import { IAdyenPaymentData, IPaymentModel, IPaymentSubmissionState } from '../../../interfaces/adyen-payment.interface';
import CardElement from '@adyen/adyen-web/dist/types/components/Card';
import { parseRGB, RGBToHex } from '../../../../color-scheme-generator/modules/helpers/color.helper';
import { AdyenHandleAction } from '../../../reducers/payment.reducer.types';
import DropinElement from '@adyen/adyen-web/dist/types/components/Dropin';
import Config from '../../../config';

interface AdyenCardRegistrationType {
	currentCardNumber: string;
	adyenClientKey: string;
	adyenPaymentMethodsResponse: IPaymentModel;
	handleAction: AdyenHandleAction;
	onSubmit: (state: IPaymentSubmissionState, dropin: CardElement) => void;
	onAdditionalDetails: (state: IAdyenPaymentData, dropin: CardElement) => void;
	onCancelClick: () => void;
	onOverlayClick: () => void;
}

const inputStyle = {
	base: {
		color: '#ffffff',
		fontWeight: 'normal',
		fontStyle: 'normal',
		letterSpacing: '0.02px',
		fontFamily: ' -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, Oxygen, Ubuntu, Cantarell, \'Open Sans\',\n    \'Helvetica Neue\', sans-serif',
		fontSize: '14px',
		fontSmoothing: 'antialiased',

		'::placeholder': {
			color: 'rgba(255, 255, 255, 0.36)',
		},
		':-webkit-autofill': {
			color: '#e39f48',
		},
	},
	error: {
		color: '#ef9e99',

		'::placeholder': {
			color: 'rgba(255, 255, 255, 0.36)',
		},
	},
}

export const AdyenCardRegistration: React.FC<AdyenCardRegistrationType> = (
	{
		currentCardNumber,
		adyenClientKey,
		adyenPaymentMethodsResponse,
		handleAction,
		onSubmit,
		onAdditionalDetails,
		onCancelClick,
		onOverlayClick
	}
) => {
	const [fontColor, setFontColor] = useState<string>('#ffffff');
	const translations = useContext(TranslationsContext);
	const adyenCardRef = useRef<HTMLDivElement>(null);
	const [isValid, setIsValid] = useState(false);
	const [dropin, setDropin] = useState<DropinElement>(null);

	const [threeDSEnabled, setThreeDSEnabled] = useState<boolean>(false);

	const currentStyle = {
		...inputStyle,
		base: {
			...inputStyle.base,
			color: fontColor
		}
	}

	const onChange = (state, dropin): void => {
		setIsValid(state.isValid);
	}

	const checkout = new AdyenCheckout(getAdyenConfiguration(
			adyenPaymentMethodsResponse,
			adyenClientKey,
			Config.adyenLocale,
			Config.adyenEnvironment,
			currentStyle,
			onSubmit,
			onAdditionalDetails,
			onChange,
		)
	);


	useEffect(() => {
		try {
			const dummyDiv = document.createElement('div');
			dummyDiv.style.setProperty('background-color', 'hsl(var(--group-card-font-color-h), var(--group-card-font-color-s), var(--group-card-font-color-l))')
			dummyDiv.id = 'dummy-div';
			document.body.appendChild(dummyDiv);
			const computedColor = getComputedStyle(dummyDiv).backgroundColor;
			document.body.removeChild(dummyDiv);
			const parsedColor = parseRGB(computedColor);

			setFontColor(RGBToHex(parsedColor?.r, parsedColor?.g, parsedColor?.b));
		} catch (e) {
			setFontColor('#ffffff');
		}

	}, []);

	useEffect(() => {
		if (!adyenClientKey || !adyenPaymentMethodsResponse) {
			return;
		}
		setDropin(checkout.create('dropin').mount(adyenCardRef.current));
	}, [adyenPaymentMethodsResponse, adyenClientKey]);

	useEffect(() => {
		if (!handleAction || !dropin) {
			return;
		}
		setThreeDSEnabled(true);
		dropin.handleAction(handleAction);
	}, [handleAction]);

	return (
		<DrawerPopup
			title={
				getSafeTranslation(adyenPaymentMethodsResponse?.storedPaymentMethods?.length > 0 ?
						'AdyenCardRegistrationPopup.confirmCVC' : 'AdyenCardRegistrationPopup.addNewCreditCard',
					translations)
			}
			description={adyenPaymentMethodsResponse?.storedPaymentMethods?.length > 0 ?
				getSafeTranslation('AdyenCardRegistrationPopup.currentCardNumber', translations)
				.replace('{cardNumber}', currentCardNumber) : null}
			leftControl={
				<Button isText onClick={onCancelClick}>{
					getSafeTranslation('AdyenCardRegistrationPopup.cancelButtonCaption', translations)
				}</Button>
			}
			onOverlayClick={onOverlayClick}
			rightControl={
				!threeDSEnabled && (
					<Button onClick={() => dropin.submit()} isDisabled={!isValid} isAutoWidth>
						{getSafeTranslation(
							adyenPaymentMethodsResponse?.storedPaymentMethods?.length > 0 ?
								'AdyenCardRegistrationPopup.confirmButtonCaption' : 'AdyenCardRegistrationPopup.doneButtonCaption',
							translations)}
					</Button>)
			}
		>
			<div className="adyen-card-registration">
				<div ref={adyenCardRef}/>
			</div>
		</DrawerPopup>
	);
}

const getAdyenConfiguration = (paymentMethodsResponse, clientAdyenKey, adyenLocale, adyenEnvironment, cardStyles, onSubmit, onAdditionalDetails, onChange): CoreOptions => ({
	paymentMethodsResponse,
	clientKey: clientAdyenKey,
	locale: adyenLocale,
	environment: adyenEnvironment,
	showPayButton: false,
	paymentMethodsConfiguration: {
		card: {
			styles: {
				...cardStyles,
			},
		},
		storedCard: {
			styles: {
				...cardStyles,
			},
		},
		threeDS2: {
			challengeWindowSize: '05'
		},
		ideal: {
			showImage: false,
		},
	},
	onSubmit: onSubmit,
	onAdditionalDetails: onAdditionalDetails,
	onChange: onChange,
});
